import { IconHelp, IconUser, IconPhoto, IconUpload } from "@tabler/icons-react";
import React, { useState, useRef, useEffect } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import {
  AREA_ACTIVITY_OPTIONS,
  GENDER_OPTIONS,
  PERSONALITY_OPTIONS,
} from "../AgentsConstants";
import { Controller } from "react-hook-form";
import TemplateSelector from "./TemplateSelector";

// Componente Step 1: Identidade do Agente
const StepIdentity = React.memo(
  ({ register, watch, setValue, errors, control, isEditing }) => {
    const [agentImage, setAgentImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageError, setImageError] = useState("");
    const [showTemplateSelector, setShowTemplateSelector] = useState(!isEditing);
    const [templateApplied, setTemplateApplied] = useState(false);
    const fileInputRef = useRef(null);

    // Watch do valor agent_image do formulário
    const watchedAgentImage = watch("agent_image");

    // Sincronizar imagePreview com o valor do formulário
    useEffect(() => {
      // Reset de erros ao mudar imagem
      setImageError("");
      
      if (watchedAgentImage) {
        if (typeof watchedAgentImage === 'string') {
          // É uma URL da API (modo edição)
          setImagePreview(watchedAgentImage);
          setAgentImage(null); // Não é um arquivo local
        } else if (watchedAgentImage instanceof File) {
          // Usar o nome e tamanho do arquivo para comparação mais confiável
          const currentFileKey = `${watchedAgentImage.name}-${watchedAgentImage.size}-${watchedAgentImage.lastModified}`;
          const previousFileKey = agentImage ? `${agentImage.name}-${agentImage.size}-${agentImage.lastModified}` : null;
          
          // Só processar se é um arquivo diferente do anterior
          if (currentFileKey !== previousFileKey) {
            const reader = new FileReader();
            reader.onload = (e) => {
              setImagePreview(e.target.result);
            };
            reader.onerror = () => {
              setImageError("Erro ao carregar a imagem. Tente novamente.");
              setImagePreview(null);
            };
            
            try {
              reader.readAsDataURL(watchedAgentImage);
              setAgentImage(watchedAgentImage);
            } catch {
              setImageError("Erro ao processar a imagem. Tente novamente.");
              setImagePreview(null);
            }
          }
        } else {
          setImagePreview(null);
          setAgentImage(null);
        }
      } else {
        // Não há imagem
        setImagePreview(null);
        setAgentImage(null);
      }
    }, [watchedAgentImage, agentImage]);

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      setImageError("");

      if (!file) {
        setValue("agent_image", null, { shouldDirty: true });
        return;
      }

      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setImageError("Por favor, selecione apenas arquivos de imagem.");
        // Reset do input para permitir nova seleção
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Validar tamanho máximo de 1MB
      if (file.size > 1024 * 1024) {
        setImageError("A imagem deve ter no máximo 1MB.");
        // Reset do input para permitir nova seleção
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Usar setValue com shouldDirty e shouldTouch para forçar re-render
      setValue("agent_image", file, { 
        shouldDirty: true, 
        shouldTouch: true,
        shouldValidate: true 
      });
      
      // Processar preview diretamente como fallback
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setAgentImage(file);
      };
      reader.onerror = () => {
        setImageError("Erro ao carregar a imagem. Tente novamente.");
      };
      reader.readAsDataURL(file);
    };

    const removeImage = () => {
      setValue("agent_image", null, { 
        shouldDirty: true, 
        shouldTouch: true,
        shouldValidate: true 
      });
      setImagePreview(null);
      setAgentImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    // Função para aplicar template selecionado
    const handleSelectTemplate = (template) => {
      if (template.id === 'blank') {
        // Template em branco - não preenche nada
        setShowTemplateSelector(false);
        setTemplateApplied(true);
        return;
      }

      // Aplicar dados do template aos campos do formulário
      if (template.agentJob) {
        setValue("agentJob", template.agentJob);
      }
      if (template.agentGender) {
        setValue("agentGender", template.agentGender);
      }
      if (template.agentPersona) {
        setValue("agentPersona", template.agentPersona);
      }
      if (template.agentArea) {
        setValue("agentArea", template.agentArea);
      }
      if (template.language) {
        setValue("language", template.language);
      }
      if (template.languageStyle) {
        setValue("languageStyle", template.languageStyle);
      }
      if (template.fundamentalRules) {
        setValue("fundamentalRules", template.fundamentalRules);
      }
      if (template.prohibitedWords) {
        setValue("prohibitedWords", template.prohibitedWords);
      }
      if (template.preferredWords) {
        setValue("preferredWords", template.preferredWords);
      }
      if (template.businessInfoTemplate) {
        setValue("businessInfo", template.businessInfoTemplate);
      }

      // Aplicar fluxo padrão
      if (template.defaultFlowSteps) {
        setValue("defaultFlowSteps", template.defaultFlowSteps);
      }
      if (template.defaultFlowContent) {
        const currentDefaultFlow = watch("defaultFlow") || {};
        
        // Processar placeholders no conteúdo do template
        const processedContent = {};
        Object.keys(template.defaultFlowContent).forEach(key => {
          let content = template.defaultFlowContent[key];
          // Substituir placeholders básicos
          content = content.replace(/\{\{agentName\}\}/g, watch("agentName") || "[Nome do Agente]");
          content = content.replace(/\{\{businessName\}\}/g, "[Nome do Negócio]");
          content = content.replace(/\{\{serviceType\}\}/g, "[Tipo de Serviço]");
          content = content.replace(/\{\{price\}\}/g, "[Preço]");
          processedContent[key] = content;
        });
        
        setValue("defaultFlow", {
          ...currentDefaultFlow,
          ...processedContent
        });
      }

      // Aplicar interações
      if (template.interactions) {
        setValue("interactions", template.interactions);
      }

      // Aplicar configurações de funções se houver
      if (template.mainFunction) {
        setValue("functionsEnabled", true);
        
        // Configurar função principal baseada no template
        const functionSchema = {
          [template.mainFunction]: {
            enabled: true,
            useDefault: false, // Permitir que usuário escolha resposta automática ou personalizada
            customResponse: "",
            registerClientData: true,
            registerContactData: true,
            fields: {},
            customFields: template.customFields || []
          }
        };

        // Configurar campos padrão da função se houver
        if (template.functionFields && Array.isArray(template.functionFields)) {
          template.functionFields.forEach(fieldId => {
            functionSchema[template.mainFunction].fields[fieldId] = {
              enabled: true,
              required: true
            };
          });
        }

        setValue("functionSchemas", functionSchema);
      }

      setShowTemplateSelector(false);
      setTemplateApplied(true);
    };

    // Função para mostrar novamente o seletor de templates
    const handleShowTemplateSelector = () => {
      setShowTemplateSelector(true);
    };

    // Verificar se já tem dados preenchidos para mostrar no TemplateSelector
    const currentValues = {
      agentName: watch("agentName"),
      agentJob: watch("agentJob"),
      agentGender: watch("agentGender"),
      agentPersona: watch("agentPersona"),
      agentArea: watch("agentArea")
    };

    const hasCurrentData = Object.values(currentValues).some(value => 
      value && (Array.isArray(value) ? value.length > 0 : value.trim() !== "")
    );
    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <IconUser className="me-2 text-primary" size={24} />
            <h4 className="mb-0">Identidade do Agente</h4>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>Defina quem é seu agente e como ele se comporta</Tooltip>
              }
            >
              <IconHelp
                className="ms-2 text-muted"
                size={16}
                style={{ cursor: "help" }}
              />
            </OverlayTrigger>
          </div>
          
          {!showTemplateSelector && !isEditing && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleShowTemplateSelector}
            >
              Alterar Template
            </Button>
          )}
        </div>

        {/* Template Selector */}
        {showTemplateSelector && (
          <TemplateSelector 
            onSelectTemplate={handleSelectTemplate} 
            currentValues={hasCurrentData ? currentValues : {}}
          />
        )}

        {/* Alert mostrando template aplicado */}
        {templateApplied && !showTemplateSelector && (
          <Alert variant="success" className="mb-4">
            <strong>Template aplicado com sucesso!</strong> Os campos foram preenchidos automaticamente. 
            Você pode editá-los conforme necessário.
          </Alert>
        )}
        
        {/* Campos principais - só aparecem quando template for selecionado, se já tiver dados, ou se estiver editando */}
        {(!showTemplateSelector || hasCurrentData || isEditing) && (
          <>
            {/* Campo de Upload da Foto do Perfil */}
            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
              <Form.Label>
                <IconPhoto className="me-2" size={16} />
                Foto do Perfil
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>Adicione uma foto para o perfil do agente (máximo 1MB)</Tooltip>
                  }
                >
                  <IconHelp
                    className="ms-1 text-muted"
                    size={14}
                    style={{ cursor: "help" }}
                  />
                </OverlayTrigger>
              </Form.Label>
              
              <div className="d-flex align-items-start gap-3">
                {/* Preview da imagem ou placeholder */}
                <div 
                  className="border rounded-circle d-flex align-items-center justify-content-center"
                  style={{ 
                    width: "100px", 
                    height: "100px", 
                    backgroundColor: "#f8f9fa",
                    overflow: "hidden"
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview do agente"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <IconUser size={40} className="text-muted" />
                  )}
                </div>

                {/* Controles de upload */}
                <div className="flex-grow-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                  
                  <div className="d-flex gap-2 mb-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IconUpload className="me-1" size={16} />
                      {imagePreview ? "Alterar Foto" : "Selecionar Foto"}
                    </Button>
                    
                    {imagePreview && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={removeImage}
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                  
                  <small className="text-muted">
                    Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 1MB
                  </small>
                  
                  {imageError && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2">
                      {imageError}
                    </Alert>
                  )}
                </div>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Agente *</Form.Label>
              <Form.Control
                {...register("agentName")}
                placeholder="Ex: Maria Atendimento"
                isInvalid={!!errors.agentName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.agentName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Função</Form.Label>
              <Form.Control
                {...register("agentJob")}
                placeholder="Ex: secretária, atendente virtual..."
                isInvalid={!!errors.agentJob}
                maxLength={30}
              />
              <Form.Control.Feedback type="invalid">
                {errors.agentJob?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Gênero *</Form.Label>
              <Controller
                name="agentGender"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={GENDER_OPTIONS}
                    placeholder="Selecione o gênero..."
                    value={GENDER_OPTIONS.find(
                      (opt) => opt.value === watch("agentGender")
                    )}
                    onChange={(selected) =>
                      setValue("agentGender", selected?.value || "")
                    }
                    className={errors.agentGender ? "is-invalid" : ""}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "56px",
                        borderRadius: "1.25rem",
                        borderColor: errors.agentGender
                          ? "#dc3545"
                          : state.isFocused
                          ? "#86b7fe"
                          : "#dee2e6",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                          : "none",
                        "&:hover": {
                          borderColor: errors.agentGender
                            ? "#dc3545"
                            : "#b0b8c1",
                        },
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#6c757d",
                      }),
                    }}
                  />
                )}
              />
              {errors.agentGender && (
                <div className="invalid-feedback d-block">
                  {errors.agentGender?.message}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Área de Atuação *</Form.Label>
              <Controller
                name="agentArea"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={AREA_ACTIVITY_OPTIONS}
                    placeholder="Selecione a área..."
                    isSearchable
                    value={AREA_ACTIVITY_OPTIONS.find(
                      (opt) => opt.value === watch("agentArea")
                    )}
                    onChange={(selected) =>
                      setValue("agentArea", selected?.value || "")
                    }
                    className={errors.agentArea ? "is-invalid" : ""}
                    menuPortalTarget={document.body} // Renderiza o menu no body
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "56px",
                        borderRadius: "1.25rem",
                        borderColor: errors.agentArea
                          ? "#dc3545"
                          : state.isFocused
                          ? "#86b7fe"
                          : "#dee2e6",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                          : "none",
                        "&:hover": {
                          borderColor: errors.agentArea ? "#dc3545" : "#b0b8c1",
                        },
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#6c757d",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999, // Z-index muito alto para sobrepor o modal
                      }),
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999, // Garante que o menu fique acima de outros elementos
                      }),
                    }}
                  />
                )}
              />
              {errors.agentArea && (
                <div className="invalid-feedback d-block">
                  {errors.agentArea?.message}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>
            Personalidade do Agente *
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>É como você descreveria o jeito dela falar</Tooltip>
              }
            >
              <IconHelp
                className="ms-1 text-muted"
                size={14}
                style={{ cursor: "help" }}
              />
            </OverlayTrigger>
          </Form.Label>
          <Controller
            name="agentPersona"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="d-flex flex-wrap gap-2">
                {PERSONALITY_OPTIONS.map((personality) => (
                  <Form.Check
                    key={personality}
                    type="checkbox"
                    id={`personality-${personality}`}
                    label={personality}
                    checked={value?.includes(personality) || false}
                    onChange={(e) => {
                      const newValue = value || [];
                      if (e.target.checked) {
                        onChange([...newValue, personality]);
                      } else {
                        onChange(newValue.filter(p => p !== personality));
                      }
                    }}
                    className="me-3"
                  />
                ))}
              </div>
            )}
          />
          {errors.agentPersona && (
            <div className="text-danger small mt-1">
              {errors.agentPersona.message}
            </div>
          )}
        </Form.Group>
        </>
        )}
      </div>
    );
  }
);

StepIdentity.displayName = "StepIdentity";

export default StepIdentity;
